import { FC, useEffect, useMemo } from "react";
import { useOrder } from "@/hooks/useOrder";
import { useOrdersStore } from "@/pages/orders-new/use-orders-store";
import { User, MapPin, Clock, MessageSquare, Bike, Phone } from "lucide-react";
import { getOrderStatusDisplay } from "@/lib/statusConfig";
import { useNavigate } from "react-router-dom";

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  realTime?: any;
}

const OrderModal: FC<OrderModalProps> = ({
  isOpen,
  onClose,
  orderId,
  realTime,
}) => {
  // Fetch order data
  const { order, loading, error } = useOrder(orderId || null);
  const { setOrderDetail } = useOrdersStore();
  const navigate = useNavigate();
  useEffect(() => {
    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Helper function to get driver status
  const getDriverStatus = () => {
    const statusConfig = getOrderStatusDisplay(
      realTime?.driver_status
        ? realTime.driver_status.replaceAll("_", " ")
        : order?.status || "",
    );
    return statusConfig.display;
  };

  const currentAndPreviousDriver = useMemo(() => {
    const list = (order as any)?.driver_assignments || [];
    if (!Array.isArray(list) || list.length === 0)
      return { current: null, previous: null };
    const current = list[list.length - 1];
    const previous = list.length > 1 ? list[list.length - 2] : null;
    return { current, previous };
  }, [order]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 pb-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium text-gray-900">
              Orden {order?.tracking_id || "N/A"}
            </h2>
            <span className="text-xs text-blue-500 px-2 py-0.5 border-blue-500 border rounded-full">
              {(order as any)?.branches?.name || "Sucursal"}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="px-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">
                Cargando orden...
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2 text-sm">
                Error al cargar la orden
              </div>
              <div className="text-xs text-gray-500">{error}</div>
            </div>
          ) : order ? (
            <>
              {/* Detalles del pedido */}
              <div className="border p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">
                    Detalles del pedido
                  </h3>
                  <button
                    type="button"
                    className="text-sm text-blue-600 font-medium hover:underline"
                    onClick={() => {
                      // Set the order as selected in the store
                      setOrderDetail(order.id);
                      // Close the modal
                      onClose();
                      // Navigate to orders page
                      navigate(`/orders/${order.id}`);
                    }}
                  >
                    Ver orden
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1 flex justify-start items-center gap-1">
                      <span className="text-sm font-medium text-gray-800">
                        Cliente:
                      </span>{" "}
                      <span className="text-sm text-gray-800">
                        {(order as any)?.customers?.full_name || "Juan Perez"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1 flex justify-start items-center gap-1">
                      <span className="text-sm font-medium text-gray-800">
                        Teléfono:
                      </span>{" "}
                      <span className="text-sm text-gray-800">
                        {order.customer?.phone || "-"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center mt-0.5">
                      <MapPin className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1 flex justify-start items-center gap-1">
                      <span className="text-sm font-medium text-gray-800">
                        Dirección de entrega:
                      </span>{" "}
                      <span className="text-gray-600">
                        {(order as any)?.addresses?.full_address ||
                          "Mannington,wv, 26582 Honduras"}
                      </span>
                    </div>
                  </div>

                  {order.created_at && (
                    <div className="flex items-center">
                      <div className="w-6 h-6 mr-3 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1 flex justify-start items-center gap-1">
                        <span className="text-sm font-medium text-gray-800">
                          Creado
                        </span>{" "}
                        <span className="text-sm text-gray-800">
                          {order.created_at
                            ? new Date(order.created_at).toLocaleString(
                                "es-SV",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  timeZone: "America/El_Salvador",
                                },
                              )
                            : "-"}
                        </span>
                      </div>
                    </div>
                  )}

                  {order.comment && (
                    <div className="flex items-center">
                      <div className="w-6 h-6 mr-3 flex items-center justify-center">
                        <MessageSquare className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1 flex justify-start items-center gap-1">
                        <span className="text-sm font-medium text-gray-800">
                          Comentario:
                        </span>{" "}
                        <span className="text-sm text-gray-800">
                          {order.comment || "-"}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center">
                      <Bike className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1 flex justify-start items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        Conductor:
                      </span>{" "}
                      {currentAndPreviousDriver.current &&
                      currentAndPreviousDriver.previous ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-800">
                            {currentAndPreviousDriver.current?.driver?.name ||
                              "Sin asignar"}
                          </span>
                          <span className="text-xs text-gray-500">
                            (historial incluye conductor anterior)
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-800">
                          {currentAndPreviousDriver.current?.driver?.name ||
                            (order as any)?.drivers?.name ||
                            "Sin asignar"}
                        </span>
                      )}
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {getDriverStatus()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumen de pago */}
              <div className="border p-4 rounded-lg">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="flex-1 flex justify-between">
                      <span className="text-sm text-gray-800">
                        Medio de pago:
                      </span>
                      <span className="text-sm text-gray-800">
                        {order.paid_with === "cash" ? "Efectivo" : "Tarjeta"}
                      </span>
                    </div>
                  </div>

                  {[
                    {
                      label: "Subtotal",
                      value: order.subtotal,
                      valueClass: "text-gray-800",
                      prefix: "",
                    },
                    {
                      label: "Impuestos",
                      value: order.tax,
                      valueClass: "text-gray-800",
                      prefix: "",
                    },
                    {
                      label: "Costo de envío",
                      value: order.total_delivery_charge,
                      valueClass: "text-gray-800",
                      prefix: "",
                    },
                    {
                      label: "Descuento",
                      value: order.discount,
                      valueClass: "text-green-600",
                      prefix: "-",
                    },
                  ].map((item) => (
                    <div className="flex items-center" key={item.label}>
                      <div className="flex-1 flex justify-between">
                        <span className="text-sm text-gray-600">
                          {item.label}:
                        </span>
                        <span className={`text-sm ${item.valueClass}`}>
                          {item.prefix}
                          {order.currency || "$"}{" "}
                          {item.value?.toFixed(0) ?? "0"}
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center pt-2 border-t">
                    <div className="flex-1 flex justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        Total:
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {order.currency || "$"}{" "}
                        {(
                          (order.subtotal || 0) +
                          (order.tax || 0) +
                          (order.total_delivery_charge || 0) -
                          (order.discount || 0)
                        ).toFixed(0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-sm text-gray-500">
              No se encontró información de la orden
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderModal;
